/**
 * Security Graph API module.
 *
 * Real per-domain split from services/api.ts. Verbatim extraction of the 6
 * graph-domain API objects; behavior is unchanged. Other graph-adjacent
 * exports (e.g. crossCloudApi) remain in api.ts and are re-exported by
 * services/api/index.ts.
 */
import { _unwrap, _thenUnwrap } from './_unwrap';
// `apiClient` is a NAMED export from ./client (verified against vulnerability/ai/easm
// sibling splits). The earlier default-import form would fail at runtime.
import { apiClient as fetchClient } from './client';
import { getCurrentTenantId } from '../../contexts/TenantContext';
import {
  AttackPath,
  PaginatedResponse,
  SeverityLevel,
} from '../../types';

// The graph-engine (port 8098) intermittently starves its own event loop while
// its background sync/topology loops run (see graph-engine-eventloop-starvation
// note), so graph-backed reads can stall for tens of seconds. These are
// read-only dashboard loads where a fast empty result is far better than a long
// spinner or a thrown error — race each against an 8s cap and fall back to a
// safe empty shape. Healthy responses return in <1s, so there is ample headroom.
const _graphTimeout = <T,>(p: Promise<T>, fallback: T, ms = 8000): Promise<T> =>
  Promise.race([
    p.catch(() => fallback),
    new Promise<T>((res) => setTimeout(() => res(fallback), ms)),
  ]);

// ============================================================================
// Security Graph API (Attack Paths, Toxic Combinations)
// ============================================================================
export const securityGraphApi = {
  getStats: async (): Promise<any> => {
    return _graphTimeout((async () =>
      _unwrap(await fetchClient.get<any>('/api/v1/graph/stats')))(), {});
  },

  getAttackPaths: async (params?: {
    severity?: SeverityLevel;
    page?: number;
    page_size?: number;
    limit?: number;
  }): Promise<PaginatedResponse<AttackPath>> => {
    // This endpoint paginates on `limit`; a `page_size` here is rewritten by
    // normalizePageSizeParam() in client.ts (see the measured table there —
    // ?page_size=200 cost 13.0s/3.96MB versus 1.36s/1.62MB for ?limit=200).
    return _graphTimeout((async () =>
      _unwrap(await fetchClient.get<PaginatedResponse<AttackPath>>('/api/v1/attack-paths', params as any)))(),
      { items: [], total: 0, page: 1, page_size: 0, total_pages: 0 } as any);
  },

  getToxicCombinations: async (params?: { limit?: number }): Promise<{ items: any[]; total: number }> => {
    // Backend (graph-engine) returns a {items:[...], total:N} envelope. The `items`
    // array is LIMIT-capped (page), so its length is NOT the real combo count —
    // counting it reported 500/100 (the cap) instead of the true ~711. Preserve
    // `total` (the unbounded COUNT(*) the endpoint already computes) so tiles show
    // the canonical count regardless of the fetch limit.
    return _graphTimeout((async () => {
      const res: any = _unwrap(await fetchClient.get<any>('/api/v1/toxic-combinations', params as any));
      const items = Array.isArray(res) ? res : (res?.items ?? res?.toxic_combinations ?? []);
      return { items, total: Number(res?.total ?? items.length) };
    })(), { items: [], total: 0 });
  },

  // R157 — suppress / unsuppress a toxic combination by id. Sets status in
  // csp_core.toxic_combinations so the issue-engine bridge doesn't re-emit
  // issues for it on the next graph-rule eval pass.
  suppressToxicCombo: async (id: string, payload?: { reason?: string; actor?: string; suppress_until?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/toxic-combinations/${id}/suppress`, payload || {}));
  },
  unsuppressToxicCombo: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/toxic-combinations/${id}/unsuppress`, {}));
  },

  calculateBlastRadius: async (resourceId: string): Promise<any> => {
    // BE route is GET-only with a query string (misc.py:674); a POST body 405s.
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/blast-radius', { resource_id: resourceId }));
  },

  getGraph: async (params?: { depth?: number }): Promise<any> => {
    return _graphTimeout((async () =>
      _unwrap(await fetchClient.get<any>('/api/v1/graph', params as any)))(),
      { nodes: [], edges: [] });
  },

  getAttackPath: async (id: string): Promise<AttackPath> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/attack-paths/${id}`));
  },

  analyzeAttackPaths: async (params?: { scope?: string; depth?: number }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/attack-paths/analyze', params));
  },

  getCrownJewels: async (params?: { page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/crown-jewels', params as any));
  },

  getBlastRadius: async (resourceId: string, params?: { depth?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/graph/blast-radius/${resourceId}`, params as any));
  },

  getGraphVisualization: async (params?: { depth?: number; filter_type?: string }): Promise<any> => {
    // /api/v1/graph/visualization has no handler on graph-engine (404). The
    // canonical wiz-graph visualization payload (nodes + edges) is served by
    // /api/v1/graph — repoint so any caller receives real data, not a 404.
    return _graphTimeout((async () =>
      _unwrap(await fetchClient.get<any>('/api/v1/graph', params as any)))(),
      { nodes: [], edges: [] });
  },

  getGraphNodes: async (params?: { type?: string; risk_min?: number }): Promise<any[]> => {
    return _graphTimeout((async () =>
      _unwrap(await fetchClient.get<any>('/api/v1/graph/nodes', params as any)))(), []);
  },

  getGraphStatistics: async (): Promise<any> => {
    return _graphTimeout((async () =>
      _unwrap(await fetchClient.get<any>('/api/v1/graph/statistics')))(), {});
  },

  getMITREMappings: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/mitre-mappings'));
  },

  getMITRETacticDetail: async (tacticId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/graph/mitre-mappings/${tacticId}`));
  },

  getAttackPathTemplates: async (params?: { category?: string; provider?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/attack-paths/templates', params as any));
  },

  getAttackPathTemplate: async (templateId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/attack-paths/templates/${templateId}`));
  },

  getPriorityQueue: async (params?: { limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/attack-paths/priority-queue', params as any));
  },

  getAttackPathTrends: async (params?: { time_range?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/attack-paths/trending', params as any));
  },

  simulateRemediation: async (params: { scenario_id?: string; changes?: any[] }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/attack-paths/simulate', params));
  },

  getWhatIfScenarios: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/attack-paths/what-if'));
  },

  getCrossCloudPaths: async (params?: { source_cloud?: string; target_cloud?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/attack-paths/cross-cloud', params as any));
  },

  getCrossCloudPath: async (pathId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/attack-paths/cross-cloud/${pathId}`));
  },

  getEntryPoints: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/entry-points'));
  },

  getCrownJewelDetail: async (jewelId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/graph/crown-jewels/${jewelId}`));
  },

  // Incremental Graph Update
  incrementalUpdate: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/graph/incremental', data));
  },

  // Multi-Cloud Graph Queries
  getAzureNodes: async (params?: { type?: string; risk_min?: number }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/nodes', { ...params, provider: 'azure' } as any));
  },

  getGCPNodes: async (params?: { type?: string; risk_min?: number }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/nodes', { ...params, provider: 'gcp' } as any));
  },

  getAWSNodes: async (params?: { type?: string; risk_min?: number }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/nodes', { ...params, provider: 'aws' } as any));
  },

  getAttackPathsByCloud: async (provider: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/attack-paths/templates', { provider } as any));
  },

  getToxicCombinationsByCloud: async (provider: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/toxic-combinations', { provider } as any));
  },

  // ========================================================================
  // Wiz Parity - New Attack Path Analysis Endpoints
  // ========================================================================

  // Full-Text Graph Search
  searchGraph: async (q: string, params?: { entity_type?: string; provider?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/search', { q, ...params } as any));
  },

  // Critical Attack Paths
  getCriticalAttackPaths: async (params?: { min_risk_score?: number; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/attack-paths/critical', params as any));
  },

  // Internet-Exposed Paths
  getInternetExposedPaths: async (params?: { target_type?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/attack-paths/internet-exposed', params as any));
  },

  // Attack Path Statistics
  getAttackPathStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/attack-paths/statistics'));
  },

  // Toxic Combination Rules
  getToxicCombinationRules: async (params?: { category?: string; severity?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/toxic-combinations/rules', params as any));
  },

  // Evaluate Toxic Combinations
  evaluateToxicCombinations: async (resourceId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/toxic-combinations/evaluate', { resource_id: resourceId }));
  },

  // Blast Radius Simulation
  simulateBlastRadius: async (params: { resource_id: string; attack_vector?: string; max_depth?: number }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/blast-radius/simulate', params));
  },

  // Critical Assets by Blast Radius
  getCriticalBlastRadiusAssets: async (params?: { min_affected?: number; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/blast-radius/critical-assets', params as any));
  },

  // Choke Points
  getChokePoints: async (params?: { min_paths_blocked?: number; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/choke-points', params as any));
  },

  getChokePointImpact: async (chokePointId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/choke-points/${chokePointId}/impact`));
  },

  // Wiz-parity choke points: ranks nodes by current critical/high attack-path
  // traversal count and persists into csp_graph.choke_points (migration 171).
  // Different from getChokePoints above (which reads pre-computed Neo4j props).
  attackPathChokePoints: async (params?: { limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/wiz-graph/choke-points', params as any));
  },

  // Wiz-parity what-if simulator: backend recomputes attack paths excluding the
  // remediated node/edges and returns paths_eliminated, risk_reduction_percent,
  // broken_chains_by_severity. Pure compute — no DB writes.
  attackPathWhatIf: async (payload: {
    remediations: { node_id: string; action: 'patch_cve' | 'close_port' | 'remove_permission' | 'isolate' }[];
  }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/wiz-graph/what-if', payload));
  },

  // Wiz-parity vulnerability path / reachability: returns the 5-leg chain
  // Internet -> Network -> Workload(CVE) -> Identity -> Crown-jewel for a
  // single finding. Missing legs are returned as null (never fabricated).
  vulnerabilityPath: async (findingId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/wiz-graph/vuln-path', { finding_id: findingId } as any));
  },

  // Kill Chain / MITRE ATT&CK
  getKillChainMapping: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/kill-chain/mapping'));
  },

  getKillChainStages: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/kill-chain/stages'));
  },

  // Lateral Movement
  getLateralMovementPaths: async (params?: { provider?: string; min_hops?: number; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/lateral-movement/paths', params as any));
  },

  getIdentityChains: async (params?: { provider?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/lateral-movement/identity-chains', params as any));
  },

  // What-If Analysis
  whatIfRemediate: async (params: { resource_id: string; action: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/what-if/remediate', params));
  },

  whatIfCompare: async (scenarios: { resource_id: string; action: string }[]): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/what-if/compare', { scenarios }));
  },

  // Crown Jewels Management
  defineCrownJewel: async (params: { resource_id: string; name: string; type?: string; provider?: string; business_impact?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/crown-jewels', params));
  },

  getCrownJewelPaths: async (jewelId: string, params?: { severity?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/crown-jewels/${jewelId}/paths`, params as any));
  },

  // Cross-Cloud Trust
  getCrossCloudTrustRelationships: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cross-cloud/trust-relationships'));
  },

  // Remediation
  getRemediationSuggestions: async (params?: { severity?: string; resource_type?: string; auto_only?: boolean; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/remediation/suggestions', params as any));
  },

  applyRemediation: async (params: { suggestion_id?: string; resource_id: string; action: string; dry_run?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/remediation/apply', params));
  },

  // AI-Powered Remediation Suggestions
  getAIRemediationSuggestion: async (data: { finding_title: string; provider: string; resource_type: string; severity?: string; resource_id?: string; region?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai/remediation-suggest', data));
  },

  // AI-Powered Issue Explanation
  getAIIssueExplanation: async (data: { title: string; severity: string; issue_type?: string; affected_asset_count?: number; is_internet_facing?: boolean; has_admin_access?: boolean; exploit_available?: boolean; compliance_frameworks?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai/explain-issue', data));
  },

  // Enhanced Trends
  getAttackPathTrendsV2: async (params?: { time_range?: string; metric?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/attack-paths/trends', params as any));
  },

  // Cross-Cloud Paths (new dedicated endpoint)
  getCrossCloudPathsEnhanced: async (params?: { source_cloud?: string; target_cloud?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cross-cloud/paths', params as any));
  },

  // Enhanced Attack Path Analysis
  analyzeAttackPathsEnhanced: async (params: { scope?: string; target_types?: string[]; max_depth?: number; include_lateral_movement?: boolean; include_privilege_escalation?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/attack-paths/analyze/enhanced', params));
  },

  // Advanced Graph Query DSL
  advancedGraphQuery: async (params: { node_filters?: any; edge_filters?: any; include_paths?: boolean; aggregations?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/graph/query/advanced', params));
  },

  // Crown Jewels Enhanced (with filtering)
  getCrownJewelsEnhanced: async (params?: { data_classification?: string; provider?: string; min_risk?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/crown-jewels', params as any));
  },

  // ---- Reverse Blast Radius ----
  // The real graph-engine route is GET /blast-radius/reverse/{resource_id}
  // (routers/blast_radius.py:375), reachable via the gateway as
  // /api/v1/blast-radius/reverse/{id} → /blast-radius/reverse/{id}. The old
  // POST /api/v1/wiz-parity/blast-radius/reverse rewrote to a nonexistent
  // /api/v1/graph/reverse and always 404'd. We normalise the real response
  // ({attacker_count, potential_attackers[], public_attackers, risky_attackers})
  // into the shape the Reverse-Blast tab reads ({total_sources, critical_sources,
  // sources[]}) so no fabrication and the UI renders real data.
  reverseBlastRadius: async (params: { asset_id: string; max_depth?: number; include_identity_paths?: boolean; include_network_paths?: boolean }): Promise<any> => {
    const raw = await _unwrap(await fetchClient.get<any>(
      `/api/v1/blast-radius/reverse/${encodeURIComponent(params.asset_id)}`,
      params.max_depth ? { max_hops: params.max_depth } : undefined,
    ));
    const attackers: any[] = Array.isArray(raw?.potential_attackers) ? raw.potential_attackers : [];
    const sources = attackers.map((a: any) => ({
      id: a.id,
      name: a.name || a.id,
      type: a.type,
      is_public: !!a.is_public,
      finding_count: a.findings ?? a.finding_count ?? 0,
      // Severity is derived from the real attacker attributes, not invented:
      // public entry points are the most critical, then those carrying findings.
      severity: a.is_public ? 'critical' : ((a.findings ?? a.finding_count ?? 0) > 0 ? 'high' : 'medium'),
    }));
    return {
      ...raw,
      total_sources: raw?.attacker_count ?? sources.length,
      critical_sources: raw?.public_attackers ?? sources.filter((s) => s.severity === 'critical').length,
      sources,
    };
  },

  getReverseBlastRadius: async (assetId: string, params?: { max_depth?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(
      `/api/v1/blast-radius/reverse/${encodeURIComponent(assetId)}`,
      params?.max_depth ? { max_hops: params.max_depth } : undefined,
    ));
  },

  // Real handler added to graph-engine: GET /blast-radius/comparison?resource_id=...
  // (routers/blast_radius.py) — returns { forward, reverse, delta, difference }.
  compareBlastRadius: async (params: { asset_id: string; max_depth?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/blast-radius/comparison', {
      resource_id: params.asset_id,
      ...(params.max_depth ? { max_hops: params.max_depth } : {}),
    } as any));
  },

  // ---- Historical Snapshots ----
  // FIX (split-store): create/list/get previously hit /api/v1/wiz-parity/snapshots
  // which the gateway routes to the CWPP VM-snapshot service (port 8005) while
  // diff/trends go to graph-engine — so created snapshot ids were never diffable.
  // All five now target the graph-engine graph-snapshot store so ids/versions
  // line up. The graph-engine create route is /api/v1/graph/snapshots/create and
  // it keys snapshots by monotonic integer `version` (not a uuid); diff/trends
  // already consume that version.
  createSnapshot: async (params?: { name?: string; description?: string; tags?: string[]; created_by?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/graph/snapshots/create', params || {}));
  },

  listSnapshots: async (params?: { page?: number; page_size?: number; sort_by?: string }): Promise<any> => {
    // graph-engine list uses `limit`; map page_size → limit.
    const limit = params?.page_size;
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/snapshots', (limit ? { limit } : {}) as any));
  },

  getSnapshot: async (snapshotId: string, _params?: { include_paths?: boolean }): Promise<any> => {
    // graph-engine addresses a point-in-time snapshot by integer version; the
    // node-history endpoint returns the recorded state for that version.
    return _unwrap(await fetchClient.get<any>(`/api/v1/graph/snapshots/${encodeURIComponent(snapshotId)}`));
  },

  diffSnapshots: async (params: { snapshot_a: string; snapshot_b: string }): Promise<any> => {
    // graph-engine diff is version-based: /api/v1/graph/snapshots/diff?version_a&version_b
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/snapshots/diff', {
      version_a: params.snapshot_a,
      version_b: params.snapshot_b,
    } as any));
  },

  getSnapshotTrends: async (params?: { time_range?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/snapshots/trends', params as any));
  },

  // ---- Cloud API Remediation Execution ----
  // FIX: the old POST /api/v1/wiz-parity/remediation/execute route never
  // existed. The real remediation-service flow is TWO steps:
  //   1. POST /api/v1/remediation/actions            → creates a durable action
  //   2. POST /api/v1/remediation/actions/{id}/execute?dry_run=<bool>
  // Step 1 persists to csp_remediation.actions and returns {action_id}; step 2
  // runs it (dry_run default true = plan-only). We return the execution result
  // merged with the created action id so the caller's rollback-by-id keeps
  // working. Throws if action creation fails (no false-success).
  executeRemediation: async (params: { resource_id: string; action: string; playbook_id?: string; dry_run?: boolean; approval_required?: boolean; parameters?: any }): Promise<any> => {
    const created = await _unwrap(await fetchClient.post<any>('/api/v1/remediation/actions', {
      name: `${params.action} on ${params.resource_id}`,
      type: params.action,
      resource_id: params.resource_id,
      playbook_id: params.playbook_id,
      parameters: params.parameters || {},
    }));
    const actionId = created?.action_id || created?.id;
    if (!actionId) {
      throw new Error(created?.error || 'Remediation action creation failed (no action_id returned)');
    }
    const dryRun = params.dry_run !== false; // default to a safe dry-run plan
    const result = await _unwrap(await fetchClient.post<any>(
      `/api/v1/remediation/actions/${encodeURIComponent(actionId)}/execute?dry_run=${dryRun}`,
      {},
    ));
    return { id: actionId, action_id: actionId, resource_id: params.resource_id, dry_run: dryRun, ...result };
  },

  listRemediationExecutions: async (params?: { status?: string; resource_id?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/wiz-parity/remediation/executions', params as any));
  },

  getRemediationExecution: async (executionId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/wiz-parity/remediation/executions/${executionId}`));
  },

  rollbackRemediation: async (executionId: string, params?: { reason?: string; rolled_back_by?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/wiz-parity/remediation/rollback/${executionId}`, params || {}));
  },

  getRemediationPlaybooks: async (params?: { provider?: string; category?: string; target_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/wiz-parity/remediation/playbooks', params as any));
  },

  // ========================================================================
  // Task 1: Graph Node Details
  // ========================================================================

  getNodeDetails: async (nodeId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/graph/nodes/${encodeURIComponent(nodeId)}`));
  },

  getNodeNeighbors: async (nodeId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/graph/nodes/${encodeURIComponent(nodeId)}/neighbors`));
  },

  getNodeContextualRisk: async (nodeId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/graph/nodes/${encodeURIComponent(nodeId)}/contextual-risk`));
  },

  // ========================================================================
  // Task 2: Temporal Graph ("What Changed")
  // ========================================================================

  getTemporalNewPaths: async (params?: { since?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/temporal/new-paths', params as any));
  },

  getTemporalRemovedPaths: async (params?: { since?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/temporal/removed-paths', params as any));
  },

  getTemporalAt: async (params: { timestamp: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/temporal/at', params as any));
  },

  // ========================================================================
  // Saved layouts — Wiz-parity: persist per-node drag positions so the view
  // the user arranges survives a page reload. Backend: csp_graph.node_layouts.
  // ========================================================================

  getGraphLayout: async (layoutName = 'default'): Promise<{ positions: Record<string, { x: number; y: number }>; total: number }> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/graph/layouts/${encodeURIComponent(layoutName)}`));
  },

  saveGraphLayout: async (
    positions: Record<string, { x: number; y: number }>,
    layoutName = 'default',
  ): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/graph/layouts/${encodeURIComponent(layoutName)}`, { positions }));
  },

  deleteGraphLayout: async (layoutName = 'default'): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/graph/layouts/${encodeURIComponent(layoutName)}`));
  },

  // ========================================================================
  // Task 3: Network Topology View
  // ========================================================================

  getNetworkTopology: async (params?: { provider?: string; region?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/network/topology', params as any));
  },

  getNetworkSecurityGroups: async (params?: { vpc_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/network/security-groups', params as any));
  },

  getNetworkVpcPeering: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/network/vpc-peering'));
  },

  // ========================================================================
  // Task 4: Graph IAM Analysis
  // ========================================================================

  getIAMEffectivePermissions: async (principalId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/graph/iam/effective-permissions/${encodeURIComponent(principalId)}`));
  },

  getIAMAccessQuery: async (params: { identity_id: string; resource_id: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/iam/access-query', params as any));
  },

  getIAMPolicyGraph: async (params?: { principal_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/iam/policy-graph', params as any));
  },

  getIAMOverprivileged: async (params?: { limit?: number; min_risk?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/iam/overprivileged', params as any));
  },

  getIAMTrustChains: async (params?: { principal_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/iam/trust-chains', params as any));
  },

  // Wiz-parity: single-call "IAM deep dive" for one principal. Combines
  // effective permissions with risk flags, trust-edge provenance,
  // cross-account + federation detection, and toxic-pattern matches.
  // Backend: /api/v1/graph/iam/deep-analysis/{principal_id}.
  getIAMDeepAnalysis: async (principalId: string, params?: { limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/graph/iam/deep-analysis/${encodeURIComponent(principalId)}`, params as any));
  },

  // ========================================================================
  // Graph Snapshots & Diff
  // ========================================================================

  createGraphSnapshot: async (params: { name: string; description?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/graph/snapshot', params));
  },

  listGraphSnapshots: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/snapshots'));
  },

  getGraphSnapshot: async (snapshotId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/graph/snapshots/${snapshotId}`));
  },

  diffGraphSnapshots: async (params: { snapshot_a: string; snapshot_b: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/diff', params as any));
  },

  // ========================================================================
  // Graph Compliance View
  // ========================================================================

  // Compliance data lives in compliance-hub (port 8070, routed via
  // gateway as /api/v1/compliance/*). The /api/v1/graph/compliance/*
  // paths these functions used before returned 404 — compliance-hub
  // already exposes frameworks + gap-analyses + assessments at the
  // canonical URLs and returns real data (8 frameworks today).
  getGraphComplianceFrameworks: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/frameworks'));
  },

  getGraphComplianceFramework: async (frameworkId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/frameworks/${frameworkId}`));
  },

  getGraphComplianceGaps: async (params?: { framework_id?: string }): Promise<any> => {
    // compliance-hub's shape: /api/v1/compliance/gap-analyses/{framework}
    const fwId = params?.framework_id || '';
    return _unwrap(await fetchClient.get<any>(
      fwId ? `/api/v1/compliance/gap-analyses/${encodeURIComponent(fwId)}`
           : '/api/v1/compliance/gap-analyses',
    ));
  },

  runGraphComplianceAssessment: async (params?: { framework_id?: string }): Promise<any> => {
    // compliance-hub's assessment endpoint is /api/v1/compliance/assessment/{framework_id}
    const fwId = params?.framework_id || '';
    if (!fwId) throw new Error('framework_id required');
    return _unwrap(await fetchClient.post<any>(`/api/v1/compliance/assessment/${encodeURIComponent(fwId)}`, {}));
  },

  // ========================================================================
  // Data Flows & Encryption Gaps
  // ========================================================================

  getGraphDataFlows: async (params?: { source_type?: string; target_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/data-flows', params as any));
  },

  getGraphEncryptionGaps: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/encryption-gaps'));
  },

  // ========================================================================
  // Graph Analytics (Importance Ranking)
  // ========================================================================

  getGraphPageRank: async (params?: { limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/analytics/pagerank', params as any));
  },

  getGraphCentrality: async (params?: { limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/analytics/centrality', params as any));
  },

  getGraphClustering: async (params?: { limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/analytics/clustering', params as any));
  },

  getGraphCommunities: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/analytics/communities'));
  },

  getGraphAnalyticsSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/analytics/summary'));
  },

  // ========================================================================
  // Attack Simulation
  // ========================================================================

  runAttackSimulation: async (params: { scenario: string; entry_point?: string; target_asset?: string; max_depth?: number }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/graph/simulation/attack', params));
  },

  getSimulationScenarios: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/simulation/scenarios'));
  },

  getSimulationResults: async (simulationId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/graph/simulation/results/${simulationId}`));
  },

  runTabletopExercise: async (params: { scenario: string; participants?: string[]; guided?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/graph/simulation/tabletop', params));
  },

  getSimulationHistory: async (params?: { limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/simulation/history', params as any));
  },

  // ========================================================================
  // Graph Queries (Custom Saved Queries - Native)
  // ========================================================================

  // Canonical CRUD lives at /api/v1/saved-queries (routers/saved_queries.py);
  // the old /api/v1/graph/queries* paths were never registered → 404 on Save.
  saveGraphQuery: async (data: { name: string; query: string; description?: string; tags?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/saved-queries', data));
  },
  listGraphQueries: async (params?: { tag?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/saved-queries', params as any));
  },
  getGraphQuery: async (queryId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/saved-queries/${queryId}`));
  },
  updateGraphQuery: async (queryId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/saved-queries/${queryId}`, data));
  },
  deleteGraphQuery: async (queryId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/saved-queries/${queryId}`));
  },
  executeGraphQuery: async (queryId: string, params?: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/saved-queries/${queryId}/run`, params));
  },

  // ========================================================================
  // Cypher Query & Export
  // ========================================================================

  cypherQuery: async (data: { query: string; params?: Record<string, any> }): Promise<any> => {
    // Backend exposes Cypher at /api/v1/graph/cypher (Wiz-parity SGQ);
    // the parameter key is 'parameters' not 'params' per the CypherQuery
    // model. Shape the payload so it matches the server contract.
    const payload = { query: data.query, parameters: data.params || {} };
    return _unwrap(await fetchClient.post<any>('/api/v1/graph/cypher', payload));
  },

  // Point-in-time graph snapshot compare + export (Wiz-parity "time travel").
  // Create/list/get handled by the earlier string-id definitions above; here
  // we only add the version-based compare endpoint and raw export.
  compareGraphSnapshots: async (versionA: number, versionB: number, tenantId = getCurrentTenantId()): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/compare', { version_a: versionA, version_b: versionB, tenant_id: tenantId } as any));
  },
  exportGraph: async (params?: { format?: string; node_types?: string; edge_types?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/export', params as any));
  },

  // ========================================================================
  // Attack Path Evidence & Identity-to-Data Paths
  // ========================================================================

  getAttackPathEvidence: async (params: { path_id: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/attack-paths/evidence', params as any));
  },
  getIdentityToDataPaths: async (params?: { identity_type?: string; data_classification?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/paths/identity-to-data', params as any));
  },
  getComplianceMapping: async (params?: { framework?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/compliance-mapping', params as any));
  },

  // ========================================================================
  // Graph Build & Refresh
  // Wiz-parity: these used to hit 3 distinct URLs (/graph/build, /graph/refresh,
  // /graph/rebuild). They now all go through the consolidated data-plane
  // primitive — `/api/v1/data-plane/refresh/graph` — which proxies to
  // graph-engine's /api/v1/graph/sync/full. Same behavior, one URL.
  // ========================================================================

  buildGraph: async (data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/data-plane/refresh/graph', data || {}));
  },
  refreshGraph: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/data-plane/refresh/graph', {}));
  },
  rebuildGraph: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/data-plane/refresh/graph', {}));
  },

  // ========================================================================
  // Paths (Find & Sensitive Data)
  // ========================================================================

  findPaths: async (params: { source_id: string; target_id: string; max_depth?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/paths/find', params as any));
  },
  getPathsToSensitiveData: async (params?: { source_type?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/paths/to-sensitive-data', params as any));
  },

  // ========================================================================
  // Graph Engine (Neo4j) Endpoints
  // ========================================================================

  getGraphEngineRiskScore: async (resourceId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/graph-engine/risk-score/${resourceId}`));
  },

  getGraphEngineToxicCombinations: async (params?: { limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph-engine/toxic-combinations', params as any));
  },

  inferEdges: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/graph-engine/infer-edges'));
  },
};

// ============================================================================
// Graph Resource API - Cross-page Neo4j integration (Issues, Inventory, DSPM)
// ============================================================================

export const graphResourceApi = {
  getAttackPaths: async (resourceId: string) => {
    return _unwrap(await fetchClient.get(`/api/v1/graph/resource/${resourceId}/attack-paths`));
  },
  getNeighbors: async (resourceId: string) => {
    return _unwrap(await fetchClient.get(`/api/v1/graph/resource/${resourceId}/neighbors`));
  },
  getBlastRadius: async (resourceId: string) => {
    return _unwrap(await fetchClient.get(`/api/v1/graph/resource/${resourceId}/blast-radius`));
  },
  getIssues: async (resourceId: string) => {
    return _unwrap(await fetchClient.get(`/api/v1/graph/resource/${resourceId}/issues`));
  },
  getAccessIdentities: async (resourceId: string) => {
    return _unwrap(await fetchClient.get(`/api/v1/graph/resource/${resourceId}/access-identities`));
  },
};

// ============================================================================
// GraphQL API
// ============================================================================

export const graphqlApi = {
  query: (query: string, variables?: Record<string, any>) =>
    fetchClient.post('/api/v1/graphql', { query, variables }).then(_thenUnwrap),
  getSchema: () =>
    fetchClient.get('/api/v1/graphql/schema').then(_thenUnwrap),
};

// ============================================================================
// Real-time Graph Updates (Events)
// ============================================================================

export const graphEventsApi = {
  // Events
  postEvent: (data: { event_type: string; entity_type: string; entity_id: string; data: Record<string, unknown>; timestamp?: string }) =>
    fetchClient.post('/api/v1/graph/events', data).then(_thenUnwrap),
  getEventLog: (params?: { entity_type?: string; entity_id?: string; event_type?: string; since?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/graph/events/log', params).then(_thenUnwrap),
  replayEvents: (data: { from_timestamp: string; to_timestamp?: string; entity_type?: string }) =>
    fetchClient.post('/api/v1/graph/events/replay', data).then(_thenUnwrap),
  getStats: () =>
    fetchClient.get('/api/v1/graph/events/stats').then(_thenUnwrap),

  // Subscriptions
  createSubscription: (data: { event_types: string[]; entity_types?: string[]; webhook_url: string; secret?: string }) =>
    fetchClient.post('/api/v1/graph/subscriptions', data).then(_thenUnwrap),
  getSubscriptions: () =>
    fetchClient.get('/api/v1/graph/subscriptions').then(_thenUnwrap),
  deleteSubscription: (subscriptionId: string) =>
    fetchClient.delete(`/api/v1/graph/subscriptions/${subscriptionId}`).then(_thenUnwrap),
};

// ============================================================================
// SaaS Entities in Security Graph
// ============================================================================

export const saasGraphApi = {
  discover: (data: { provider: string; credentials?: Record<string, unknown>; scope?: string[] }) =>
    fetchClient.post('/api/v1/graph/saas/discover', data).then(_thenUnwrap),
  getEntities: (params?: { provider?: string; entity_type?: string; risk_level?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/graph/saas/entities', params).then(_thenUnwrap),
  getEntityDetail: (entityId: string) =>
    fetchClient.get(`/api/v1/graph/saas/entities/${entityId}`).then(_thenUnwrap),
  getProviders: () =>
    fetchClient.get('/api/v1/graph/saas/providers').then(_thenUnwrap),
  getRisks: (params?: { provider?: string; severity?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/graph/saas/risks', params).then(_thenUnwrap),
  getStats: () =>
    fetchClient.get('/api/v1/graph/saas/stats').then(_thenUnwrap),
  mapToCloud: (data: { saas_entity_id: string; cloud_resource_id: string; relationship_type?: string }) =>
    fetchClient.post('/api/v1/graph/saas/map-to-cloud', data).then(_thenUnwrap),
};

// ============================================================================
// Graph Risk Engine API
// ============================================================================

export const graphRiskApi = {
  rerankFindings: (data: any) => fetchClient.post('/api/v1/graph/rerank-findings', data).then(_thenUnwrap),
  rerankFinding: (data: any) => fetchClient.post('/api/v1/graph/rerank-finding', data).then(_thenUnwrap),
  computeReachability: (data: any) => fetchClient.post('/api/v1/graph/network-reachability', data).then(_thenUnwrap),
  resolvePermissions: (data: any) => fetchClient.post('/api/v1/graph/effective-permissions', data).then(_thenUnwrap),
  buildLineage: (data: any) => fetchClient.post('/api/v1/graph/code-to-cloud/lineage', data).then(_thenUnwrap),
  traceVulnerability: (data: any) => fetchClient.post('/api/v1/graph/code-to-cloud/trace-vulnerability', data).then(_thenUnwrap),
  generateIssues: (data: any) => fetchClient.post('/api/v1/graph/generate-issues', data).then(_thenUnwrap),
  getGraphIssues: (params?: any) => fetchClient.get('/api/v1/graph/issues', params).then(_thenUnwrap),
  enrichAlert: (data: any) => fetchClient.post('/api/v1/graph/enrich-runtime-alert', data).then(_thenUnwrap),
  processCloudEvents: (data: any) => fetchClient.post('/api/v1/graph/cloud-events', data).then(_thenUnwrap),
  getEventStats: () => fetchClient.get('/api/v1/graph/cloud-events/stats').then(_thenUnwrap),
  takeSnapshot: (data: any) => fetchClient.post('/api/v1/graph/snapshots', data).then(_thenUnwrap),
  getSnapshots: () => fetchClient.get('/api/v1/graph/snapshots').then(_thenUnwrap),
  diffSnapshots: (data: any) => fetchClient.post('/api/v1/graph/snapshots/diff', data).then(_thenUnwrap),
  buildGraph: (data: any) => fetchClient.post('/api/v1/graph/build', data).then(_thenUnwrap),
};

// R126 / R127: build-jobs state-machine + risk-signals recompute + export
export const graphBuildJobsApi = {
  list: (params?: { status?: string; limit?: number }) =>
    fetchClient.get('/api/v1/graph/build-jobs', params).then(_thenUnwrap),
  get: (jobId: string) =>
    fetchClient.get(`/api/v1/graph/build-jobs/${jobId}`).then(_thenUnwrap),
  cancel: (jobId: string) =>
    fetchClient.post(`/api/v1/graph/build-jobs/${jobId}/cancel`).then(_thenUnwrap),
};

// R87 — Graph DLQ + Versions admin
export const graphDlqApi = {
  list: (params?: { status?: string; limit?: number }) =>
    fetchClient.get('/api/v1/graph/dlq', params as any).then(_thenUnwrap),
  resolve: (dlqId: string, data?: { action?: string; note?: string }) =>
    fetchClient.post(`/api/v1/graph/dlq/${dlqId}/resolve`, data || {}).then(_thenUnwrap),
};

export const graphVersionsApi = {
  list: (params?: { limit?: number }) =>
    fetchClient.get('/api/v1/graph/versions', params as any).then(_thenUnwrap),
  current: () =>
    fetchClient.get('/api/v1/graph/versions/current').then(_thenUnwrap),
};

// R131 / D-CATALOG: toxic combo rule catalog (definitions, not findings)
export const toxicComboRulesApi = {
  catalog: (params?: { provider?: string; severity?: string; category?: string; search?: string; limit?: number }) =>
    fetchClient.get('/api/v1/graph/rules/toxic-catalog', params as any).then(_thenUnwrap),
};

export const graphOpsApi = {
  recomputeRiskSignals: () =>
    fetchClient.post('/api/v1/graph/risk-signals/recompute').then(_thenUnwrap),
  recomputeNetworkTopology: () =>
    fetchClient.post('/api/v1/graph/network/recompute-topology').then(_thenUnwrap),
  recomputeK8sTopology: () =>
    fetchClient.post('/api/v1/graph/k8s/recompute-topology').then(_thenUnwrap),
  recomputeLbTopology: () =>
    fetchClient.post('/api/v1/graph/lb/recompute-topology').then(_thenUnwrap),
  recomputeVulnTopology: (minSeverity?: 'info' | 'low' | 'medium' | 'high' | 'critical') =>
    fetchClient.post(
      '/api/v1/graph/vuln/recompute-topology',
      undefined,
      { params: minSeverity ? { min_severity: minSeverity } : {} } as any,
    ).then(_thenUnwrap),
  reconcileReachability: () =>
    fetchClient.post('/api/v1/graph/network/reconcile-reachability').then(_thenUnwrap),
  refreshStaleness: () =>
    fetchClient.post('/api/v1/graph/staleness/refresh').then(_thenUnwrap),
  exportUrl: (params: { fmt: 'json' | 'graphml' | 'csv'; node_type?: string; edge_type?: string; include_stale?: boolean }) => {
    // Returns a URL the FE can hand to <a download> so the browser handles
    // the download via the same auth cookie/session. Caller is responsible
    // for prefixing API_CONFIG.BASE_URL.
    const qs = new URLSearchParams();
    qs.append('fmt', params.fmt);
    if (params.node_type) qs.append('node_type', params.node_type);
    if (params.edge_type) qs.append('edge_type', params.edge_type);
    if (params.include_stale) qs.append('include_stale', 'true');
    return `/api/v1/graph/export?${qs.toString()}`;
  },
};
